<script setup lang="ts">
import { UITag } from '@groover-dev/groover-ui'

import VText from '~/components/ui/VText.vue'

import { addHttps } from '~/mixins/global'

import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useGrowthBook } from '~/composables/useGrowthBook'

import { useMiscResizeStore } from '~/stores/miscResize'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'
import type { Platforms } from '~/types/mediaLinks'

class LocalMediaLink {
  link: string
  followers: number
  key: Platforms

  constructor(influencer: Influencer, key: Platforms) {
    this.key = key
    this.link = this.addHttps(influencer.media_links[key])
    this.followers = influencer.followers
      ? (influencer?.followers[key] ?? 0)
      : 0
  }

  private addHttps = addHttps
}

type Props = {
  influencer: Influencer
}
const { influencer } = defineProps<Props>()

const { t } = useI18n()
const { curatorIsContest } = useCuratorContestsInfos(influencer)
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const toggle = ref(false)
const isContest = curatorIsContest(influencer)

const hasAtLeastOneLink = computed<boolean>(() => {
  return Object.values(influencer.media_links).some((value) => {
    return (value?.length || 0) > 0
  })
})

const displaySecondaryOpportunities = computed<boolean>(() => {
  return (
    !!influencer.secondary_opportunities.length &&
    !influencer.secondary_opportunities.includes('__none__')
  )
})

const orderedMediaLinks = computed<LocalMediaLink[]>(() => {
  const keys = Object.keys(influencer.media_links) as Platforms[]
  const unsortedMediaLinks = keys.reduce(
    (accumulator: LocalMediaLink[], key) => {
      if (isValidMediaLink(key))
        accumulator.push(new LocalMediaLink(influencer, key))

      return accumulator
    },
    [],
  )
  return unsortedMediaLinks.sort((elemA, elemB) => {
    return elemB.followers - elemA.followers
  })
})

const displayedText = computed<string>(() => {
  const contestInfo = influencer.contest?.info ?? ''
  return isContest ? contestInfo : influencer.description
})

const longDescription = computed<boolean>(() => {
  return (
    displayedText.value.length > 240 ||
    displayedText.value.split('\n').length > 3
  )
})

const toggleValue = computed<string>(() => {
  return (toggle.value ? t('common.seeless') : t('common.seemore')) as string
})

const { isFeatureOnForGroup } = useGrowthBook()
const showNewCuratorExperience = computed(() =>
  isFeatureOnForGroup('curator-mapping', 'v2'),
)

function isValidMediaLink(key: Platforms): boolean {
  const mediaLink: string | undefined = influencer.media_links[key]
  return mediaLink !== undefined && mediaLink.length > 0 && mediaLink !== '__'
}
</script>

<template>
  <section class="descriptionMainWrapper tw-gap-6">
    <VText cfg="sans/20/bold" html-tag="h2">
      {{
        isContest
          ? $t('influencer.profile.slug.description.information')
          : $t('influencer.profile.slug.description.description')
      }}
    </VText>
    <div class="descriptionWrap">
      <span class="description preWrap" :class="{ expanded: toggle }">
        {{ displayedText }}
      </span>
    </div>
    <span v-if="longDescription" class="toggle" @click="toggle = !toggle">{{
      toggleValue
    }}</span>
    <div
      v-if="showNewCuratorExperience"
      class="tw-grid tw-grid-cols-[1fr_3fr] tw-gap-lg"
    >
      <span class="tw-font-medium">{{
        $t('influencer.profile.slug.description.main')
      }}</span>
      <div class="tw-flex tw-flex-wrap tw-gap-sm">
        <UITag
          v-for="opportunity in influencer.primary_opportunities"
          :key="opportunity"
          size="base"
          :text="$t(`opportunity.${opportunity}.name`)"
          :data-test-id="`primary-opprtunity-${opportunity}`"
        />
      </div>
      <template v-if="displaySecondaryOpportunities">
        <span class="tw-font-medium">
          {{ $t('influencer.profile.slug.description.others') }}
        </span>
        <div class="tw-flex tw-flex-wrap tw-gap-sm">
          <UITag
            v-for="opportunity in influencer.secondary_opportunities"
            :key="opportunity"
            size="sm"
            :text="$t(`opportunity.${opportunity}.secondary`)"
            :data-test-id="`secondary-opprtunity-${opportunity}`"
          />
        </div>
      </template>
    </div>
    <div class="platformsWrap">
      <a
        v-if="influencer.link.length"
        class="platform"
        :href="addHttps(influencer.link)"
        target="_blank"
        rel="noopener"
      >
        <i class="icon fas fa-link" />
        <span class="platformText">{{ influencer.link }}</span>
        <span
          v-if="SCREEN_WIDTH >= 375 && hasAtLeastOneLink"
          class="platformSeparator"
          >·</span
        >
      </a>
      <a
        v-for="(data, index) in orderedMediaLinks"
        :key="index"
        class="platform"
        :href="data.link"
        target="_blank"
        rel="noopener"
      >
        <i :class="`icon fab fa-${data.key}`" />
        <span v-if="data.followers > 0" class="platformText">
          <span>{{ data.followers }}</span>
          <span>{{ $t('common.follower', data.followers) }}</span>
        </span>
        <span
          v-if="
            SCREEN_WIDTH >= Breakpoints.xs375 &&
            index < orderedMediaLinks.length - 1
          "
          class="platformSeparator"
          >·</span
        >
      </a>
    </div>
  </section>
</template>

<style scoped lang="scss">
@media screen and (min-width: 480px) {
  .description {
    -webkit-line-clamp: 3;
  }
}

.descriptionMainWrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.descriptionWrap {
  position: relative;
  width: 100%;
}

.description {
  display: inline-block;
  color: var(--color-black);
  font-size: var(--font-size-big);
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.description.expanded {
  -webkit-line-clamp: unset;
}

.toggle {
  margin-top: 14px;
  color: var(--color-black);
  font-size: var(--font-size-big);
  font-weight: 600;
  cursor: pointer;
}

.platformsWrap {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  overflow: hidden;
  @media screen and (min-width: 375px) {
    flex-direction: row;
  }
}

.platform {
  display: flex;
  cursor: pointer;
}

.icon {
  color: var(--color-darker-grey);
  font-size: var(--font-size-big);
}

.platformText {
  margin-left: 8px;
}

.platformText,
.platformText > span {
  color: var(--color-darker-grey);
  font-size: var(--font-size-small);
}

.platformSeparator {
  margin-left: 8px;
  margin-right: 8px;
  color: var(--color-black);
  font-size: var(--font-size-big);
}
</style>
